import { transactionsDetailKeys } from "Api/Queries/Transactions/useTransactionsDetail";
import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import { useTransactionPay } from "Api/Mutations/Transactions/useTransactionPay";
import { useUpdateTransactionTip } from "Api/Mutations/Transactions/useUpdateTransactionTip";
import {
  type BankAccountListItem,
  type ErrorResponse,
  putTransactionsPublicIDTip,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { useResource } from "Translations/Resources";
import {
  type ErrorType,
  getTransactionErrorMessage,
  handleTransactionPayError,
} from "Utils/TransactionErrorsUtils";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

type SubmitArgs = {
  publicID: string;
  bankAccount: BankAccountListItem;
  tipAmount: number;
  currentTipAmount: number;
  isTipDirty: boolean;
  onPayError: (message: string | undefined) => void;
};

export const usePaymentSubmit = () => {
  const { t } = useResource();
  const queryClient = useQueryClient();
  // Stays true once a bank redirect is committed — mutation isPending flips false before the navigation completes, which would re-enable the button and make the tap look like a no-op.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { mutateAsync: payTransaction, isPending: isPaying } =
    useTransactionPay();
  const { mutateAsync: updateTransactionTip, isPending: isUpdatingTip } =
    useUpdateTransactionTip();
  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate({
    onError: () => setIsRedirecting(false),
  });

  const submit = async ({
    publicID,
    bankAccount,
    tipAmount,
    currentTipAmount,
    isTipDirty,
    onPayError,
  }: SubmitArgs) => {
    const shouldUpdateTip = isTipDirty && tipAmount !== currentTipAmount;
    let didUpdateTip = false;

    const rollbackTip = async () => {
      try {
        await processResponseWithApiProblemDetails(() =>
          putTransactionsPublicIDTip({ tipAmount: currentTipAmount }, publicID),
        )();
        queryClient.invalidateQueries({
          queryKey: transactionsDetailKeys.byPublicId(publicID),
        });
      } catch {
        // Best-effort: if the rollback itself fails we accept the stuck tip rather than
        // burying the /pay error under a secondary failure toast.
      }
    };

    try {
      if (shouldUpdateTip) {
        await updateTransactionTip({ publicID, tipAmount });
        didUpdateTip = true;
      }
      await payTransaction(
        {
          publicID,
          senderBankAccountID: bankAccount.bankAccountID,
        },
        {
          onSuccess: ({ signingUrl, isServerAuthorized }) => {
            // useTransactionPay.onSuccess redirects (window.location.href); lock so the button stays loading through that navigation.
            if (signingUrl || isServerAuthorized) {
              setIsRedirecting(true);
            }
          },
          onError: async (error) => {
            const e = error as unknown as ErrorType;
            const isPispRedirect = !!e.data?.errors.pispAuthentication;
            if (isPispRedirect) {
              setIsRedirecting(true);
              fetchAndRedirectToPISPURL({
                environment: bankAccount.environment,
                transactionPublicID: publicID,
              });
            } else if (didUpdateTip) {
              await rollbackTip();
            }
            onPayError(getTransactionErrorMessage(e, t));
          },
        },
      );
    } catch (e) {
      handleTransactionPayError(e as ErrorResponse, t);
    }
  };

  const isSubmitting = isPaying || isUpdatingTip || isRedirecting;

  return {
    submit,
    isSubmitting,
  };
};
